import React, { useEffect } from 'react';
import { Box, Grid, Typography, IconButton, Tooltip, Button } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ModalForms from '../../Shared/Modals/index.js';
import EntityDropdown from '../../Shared/EntityDropdown/EntityDropdown.tsx';
import SearchableDropdown from '../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import { EntityActions } from '../../../Services/_actions/Entity/entity.actions.ts';
import { useRecoilState } from 'recoil';
import { entityPreviewDataAtom } from '../../../Services/_states/index.js';

type ErrorsMap = { [key: number]: any };

interface EntitySettingsModalProps {
  open: boolean;
  onClose: () => void;
  onSave?: () => void;

  /** Which attribute is being edited + its index */
  attribute: any;
  index: number;

  /** Parent state & helpers */
  errors: ErrorsMap;
  leadsFormItems: any[];
  // entityPreviewData: any;
  // setEntityPreviewData: (v: any) => void;
  handleAttributeChange: (updatedAttr: any, index: number) => void;
  handleAddFieldMapping: (attrIndex: number) => void;

  /** Utility from parent (kept here to avoid duplicate logic) */
  modifyEntityFormFieldsArray: (list: any[]) => Array<{ name: string; label: string }>;
}

const EntitySettingsModal: React.FC<EntitySettingsModalProps> = ({
  open,
  onClose,
  onSave,
  attribute,
  index,
  errors,
  leadsFormItems,
  // entityPreviewData,
  // setEntityPreviewData,
  handleAttributeChange,
  handleAddFieldMapping,
  modifyEntityFormFieldsArray,
}) => {
  const entityId = attribute?.entity?._id;
  const entityActions = EntityActions();
  const [entityPreviewData, setEntityPreviewData] = useRecoilState(entityPreviewDataAtom);

  const getEntityFormById = async (entity) => {
    try {
      // if (!entity?._id) return;
      // if (entityPreviewData?.[entity?._id]) return;
      const response = await entityActions?.getFormById(entity?._id);
      if (response?.status === 200 && response?.data?.items) {
        const form = response?.data?.items;
        setEntityPreviewData({ ...entityPreviewData, [entity?._id]: form });
      }
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    if (attribute?.entity?._id) {
      getEntityFormById(attribute?.entity);
    }
  }, [attribute?.entity]);


  const body = (
    <>
      <Box sx={{ mt: 1 }}>
        <EntityDropdown
          value={attribute?.entity || null}
          onChange={(val) => {
            handleAttributeChange(
              {
                ...attribute,
                entity: { _id: val?._id || '', name: val?.name || '' },
                selectedEntityFields: [],
                query_parameter: attribute.query_parameter?.length
                  ? attribute.query_parameter
                  : [{ param: '', matched_with: '' }],
              },
              index
            );
            if (val?._id) {
              setEntityPreviewData({ ...entityPreviewData, [val?._id]: val ?? null });
            }
          }}
          label="Select Entity"
          error={!!errors[index]?.entity}
          errorMessage={errors[index]?.entity || ''}
        />
      </Box>

      {entityId && (entityPreviewData[entityId]?.form_fields?.length ?? 0) > 0 && (
        <Box sx={{ mt: 2 }}>
          {attribute.query_parameter?.map((mapping: any, mapIndex: number) => (
            <Grid container spacing={2} key={`mapping-${mapIndex}`} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <SearchableDropdown
                  label="Select Param"
                  options={[
                    { alias_name: 'global_phone_number', key_name: 'global_phone_number' },
                    ...leadsFormItems.filter((_, i) => i !== 0 && i !== index && i! <= index),
                  ]}
                  labelKey="key_name"
                  valueKey="alias_name"
                  value={
                    mapping.param ? { alias_name: mapping.param, key_name: mapping.param } : null
                  }
                  onChange={(selected) => {
                    const updatedMappings = [...(attribute.query_parameter || [])];
                    updatedMappings[mapIndex] = {
                      ...updatedMappings[mapIndex],
                      param: selected?.alias_name || '',
                      entityId: selected?.entity?._id ?? '',
                    };
                    if (selected?.key_type !== 'ENTITY') {
                      delete updatedMappings[mapIndex]['entity_form_field'];
                      delete updatedMappings[mapIndex]['entityId'];
                    }
                    handleAttributeChange(
                      { ...attribute, query_parameter: updatedMappings },
                      index
                    );
                  }}
                />
              </Grid>

              {mapping?.entityId &&
                modifyEntityFormFieldsArray(
                  entityPreviewData?.[mapping?.entityId]?.form_fields ?? []
                ).length > 0 && (
                  <Grid item xs={6}>
                    <SearchableDropdown
                      options={modifyEntityFormFieldsArray(
                        entityPreviewData?.[mapping?.entityId]?.form_fields ?? []
                      )}
                      value={
                        mapping?.entity_form_field
                          ? entityPreviewData[mapping?.entityId]?.form_fields?.find(
                            (f: any) => f.name === mapping?.entity_form_field
                          )
                          : null
                      }
                      onChange={(selected) => {
                        const updatedMappings = [...(attribute.query_parameter || [])];
                        updatedMappings[mapIndex] = {
                          ...updatedMappings[mapIndex],
                          entity_form_field: selected?.name || '',
                        };
                        handleAttributeChange(
                          { ...attribute, query_parameter: updatedMappings },
                          index
                        );
                      }}
                      label={
                        <Typography>
                          Select Previous Node Entity Form Field{' '}
                          <span style={{ color: 'red' }}>*</span>
                        </Typography>
                      }
                    />
                  </Grid>
                )}

              <Grid item xs={6}>
                <SearchableDropdown
                  label="Select Form Field"
                  options={entityPreviewData[entityId]?.form_fields ?? []}
                  valueKey="name"
                  value={
                    mapping.matched_with
                      ? entityPreviewData[entityId]?.form_fields?.find(
                        (f: any) => f.name === mapping?.matched_with
                      )
                      : null
                  }
                  onChange={(selected) => {
                    const updatedMappings = [...(attribute.query_parameter || [])];
                    updatedMappings[mapIndex] = {
                      ...updatedMappings[mapIndex],
                      matched_with: selected?.name || '',
                    };
                    handleAttributeChange(
                      { ...attribute, query_parameter: updatedMappings },
                      index
                    );
                  }}
                />
              </Grid>

              <Grid item xs={2}>
                {attribute.query_parameter?.length > 1 && (
                  <Tooltip title="Remove Mapping">
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => {
                        const updatedMappings = [...(attribute.query_parameter || [])];
                        updatedMappings.splice(mapIndex, 1);
                        handleAttributeChange(
                          { ...attribute, query_parameter: updatedMappings },
                          index
                        );
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Grid>
            </Grid>
          ))}

          <IconButton color="primary" onClick={() => handleAddFieldMapping(index)}>
            <AddIcon />
          </IconButton>

          <Grid mt={2}>
            <SearchableDropdown
              options={modifyEntityFormFieldsArray(entityPreviewData[entityId]?.form_fields ?? [])}
              // value={
              //   (entityPreviewData[entityId]?.form_fields ?? [])?.filter((field: any) =>
              //     attribute.selectedEntityFields?.includes(field?.name)
              //   ) ?? []
              // }
              value={(attribute.selectedEntityFields || []).map((fieldName: string) =>
                (entityPreviewData[entityId]?.form_fields ?? []).find(
                  (f: any) => f.name === fieldName
                )
              )}
              onChange={(e) => {
                const updateArr = (e || []).map((i: any) => i.name);
                handleAttributeChange({ ...attribute, selectedEntityFields: updateArr }, index);
              }}
              label={
                <Typography>
                  Retrieve Entity Field <span style={{ color: 'red' }}>*</span>
                </Typography>
              }
              multiSelect
              valueKey="name"
              getOptionDisabled={() => (attribute?.selectedEntityFields ?? [])?.length > 1}
            />
          </Grid>
        </Box>
      )}

      {entityId && (entityPreviewData[entityId]?.form_fields?.length ?? 0) === 0 && (
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
          Selected entity has no form fields defined.
        </Typography>
      )}
    </>
  );

  return (
    <ModalForms
      show={open}
      handleClose={onClose}
      modalHeader="Entity Data Setting"
      size="lg"
      modalBody={body}
      modalFooter={
        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="outlined" onClick={onClose} size="small">
            Close
          </Button>
          <Button variant="contained" onClick={onSave || onClose} size="small">
            Save
          </Button>
        </Box>
      }
    />
  );
};

export default EntitySettingsModal;
